import { ptBR as corePtBR } from '@mui/material/locale'
import { createTheme, PaletteMode, responsiveFontSizes } from '@mui/material/styles'
import { ptBR } from '@mui/x-data-grid/locales'
import { ptBR as pickersPT } from '@mui/x-date-pickers/locales'

import { softShadows, strongShadows } from './shadows'
import typography from './typography'

const sharedSettings = {
  shape: { borderRadius: 8 },
  typography,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
          margin: 0,
          padding: 0,
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
        },
        '#nprogress .bar': {
          zIndex: '2000 !important',
        },
      },
    },
    MuiLinearProgress: {
      MuiAvatar: {
        styleOverrides: {
          fallback: {
            height: '75%',
            width: '75%',
          },
        },
      },
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '1rem',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
}

export const lightTheme = responsiveFontSizes(
  createTheme(
    {
      ...sharedSettings,
      palette: {
        mode: 'light',
        action: {
          active: '#6b778c',
        },
        background: {
          default: '#f4f5f7',
          paper: '#ffffff',
        },
        text: {
          primary: '#172b4d',
          secondary: '#6b778c',
        },
      },
      shadows: softShadows,
    },
    ptBR,
    corePtBR,
    pickersPT,
  ),
)

export const darkTheme = responsiveFontSizes(
  createTheme(
    {
      ...sharedSettings,
      palette: {
        mode: 'dark',
        background: {
          default: '#171c24',
          paper: '#222b36',
        },
        divider: 'rgba(145, 158, 171, 0.24)',
        text: {
          primary: '#ffffff',
          secondary: '#919eab',
        },
      },
      shadows: strongShadows,
    },
    ptBR,
    corePtBR,
    pickersPT,
  ),
)

const createColorTheme = (primaryColor: string, mode: PaletteMode = 'light') => {
  const baseTheme = {
    ...sharedSettings,
    palette: {
      mode,
      primary: {
        main: primaryColor,
      },
      ...(mode === 'light'
        ? {
            action: {
              active: '#6b778c',
            },
            background: {
              default: '#f4f5f7',
              paper: '#ffffff',
            },
            text: {
              primary: '#172b4d',
              secondary: '#6b778c',
            },
          }
        : {
            background: {
              default: '#171c24',
              paper: '#222b36',
            },
            divider: 'rgba(145, 158, 171, 0.24)',
            text: {
              primary: '#ffffff',
              secondary: '#919eab',
            },
          }),
    },
    shadows: mode === 'light' ? softShadows : strongShadows,
  }

  return responsiveFontSizes(createTheme(baseTheme, ptBR, corePtBR, pickersPT))
}

export const greenTheme = createColorTheme('#4caf50')
export const blueTheme = createColorTheme('#2196f3')
export const pinkTheme = createColorTheme('#e91e63')
export const purpleTheme = createColorTheme('#9c27b0')

export const greenDarkTheme = createColorTheme('#4caf50', 'dark')
export const blueDarkTheme = createColorTheme('#2196f3', 'dark')
export const pinkDarkTheme = createColorTheme('#e91e63', 'dark')
export const purpleDarkTheme = createColorTheme('#9c27b0', 'dark')

export const gridSpacing = 3
export const drawerWidth = 260
export const appDrawerWidth = 320

export default lightTheme
